/* BrandQrDialog — corporate QR branding upsell + logo management.
   One-time ₦30,000 wallet purchase puts the user's own logo at the
   centre of their door-QR poster (replaces the Guard shield). After
   purchase, logo replacements are free. */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck, Loader2, Upload, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fileToB64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => {
    const s = r.result || "";
    const i = s.indexOf(",");
    resolve(i >= 0 ? s.slice(i + 1) : s);
  };
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function BrandQrDialog({ open, onClose, qrToken, onBranded }) {
  const [status, setStatus] = useState(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPreviewUrl(null);
    (async () => {
      try {
        const r = await axios.get(`${API}/doorbell/branding`, { withCredentials: true });
        setStatus(r.data);
      } catch { toast.error("Could not load branding status."); }
    })();
  }, [open]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) {
      toast.error("Logo must be PNG, JPEG or WebP.");
      return;
    }
    if (f.size > 1024 * 1024) {
      toast.error("Logo must be under 1MB.");
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await fileToB64(file);
      await axios.post(
        `${API}/doorbell/branding/logo`,
        { image_b64: b64, mime_type: file.type },
        { withCredentials: true },
      );
      toast.success(
        status?.paid
          ? "Logo updated — your QR poster now shows the new logo."
          : "Branding activated! Your QR poster now carries your logo.",
      );
      onBranded?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const paid = !!status?.paid;
  const price = status?.price_ngn ?? 30000;
  const balance = status?.wallet_balance_ngn ?? 0;
  const canAfford = paid || balance >= price;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm rounded-sm" data-testid="brand-qr-dialog">
        <DialogTitle className="font-heading text-lg font-bold tracking-tighter flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-600" /> Brand your Scan2Call QR
        </DialogTitle>

        {paid ? (
          <p className="text-xs text-emerald-700 font-body flex items-center gap-1.5" data-testid="brand-qr-paid-note">
            <BadgeCheck className="w-3.5 h-3.5" /> Branding active — logo changes are free. Your
            perks: custom QR display name (Scan2Call card), call notes with AI insight and PDF
            export (notes icon on any call).
          </p>
        ) : (
          <>
            <p className="text-xs text-gray-600 font-body">
              Put <span className="font-bold">your company logo</span> at the centre of your door QR
              poster instead of the Guard shield. One-time fee of{" "}
              <span className="font-bold">₦{price.toLocaleString()}</span>, charged from your
              Scan2Call wallet (balance: ₦{balance.toLocaleString()}).
            </p>
            <p className="text-[11px] text-gray-600 font-body bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5" data-testid="brand-qr-name-perk">
              <span className="font-bold text-emerald-700">Also included:</span> a custom QR display
              name shown on your poster, visitor call page and missed-call notifications — plus{" "}
              <span className="font-bold">call notes</span> with AI insight (symptoms → likely
              conditions; transcripts → summaries) and printable{" "}
              <span className="font-bold">PDF export</span> for patient files or support records.
            </p>
          </>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-24 h-24 rounded-md border-2 border-dashed border-gray-300 hover:border-emerald-500 flex items-center justify-center overflow-hidden bg-gray-50 transition-colors shrink-0"
            data-testid="brand-qr-pick-btn"
          >
            {previewUrl ? (
              <img src={previewUrl} alt="Logo preview" className="w-full h-full object-contain" />
            ) : paid && qrToken ? (
              <img
                src={`${API}/doorbell/branding/logo/${qrToken}?v=${status?.logo_updated_at || ""}`}
                alt="Current logo"
                className="w-full h-full object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <Upload className="w-5 h-5 text-gray-400" />
            )}
          </button>
          <div className="text-[11px] text-gray-500 font-body space-y-1">
            <p>PNG, JPEG or WebP · under 1MB.</p>
            <p>Square logos with a transparent or white background scan best.</p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={pickFile}
            data-testid="brand-qr-file-input"
          />
        </div>

        {!paid && !canAfford && (
          <p className="text-[11px] text-red-600 font-body" data-testid="brand-qr-low-balance">
            Wallet balance too low — top up ₦{(price - balance).toLocaleString()} more first.
          </p>
        )}

        <Button
          onClick={submit}
          disabled={!file || busy || !canAfford}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-body"
          data-testid="brand-qr-submit-btn"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : null}
          {paid ? "Update logo" : `Pay ₦${price.toLocaleString()} & apply logo`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
